import { Form } from 'antd5';
import { isEmpty, isEqual, omit } from 'lodash';
import { type TableLayout } from 'rc-table/lib/interface';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { clearStaleAssetHierarchyFilters } from 'components/data/helpers/tableFilterUtils';
import { TABLE_SCROLL_WIDTH } from 'utils/constants';
import { useFeatures } from 'utils/features';

import { Actions } from './Actions';
import { type MovedRow, ClientDataTable } from './ClientDataTable';
import { CustomizedDataBanner } from './CustomizedDataBanner';
import {
  Control,
  DeferBanner,
  HeaderContainer,
  TableCard,
  TitleContainer,
} from './DataTable.style';
import { usePriority } from './prioritizable';
import { ServerDataTable, ServerDataTableContextProvider } from './ServerDataTable';
import { type ESortOptions, EFilterSortClearType } from './types';
import { useFormattedColumns } from './useFormattedColumns';
import { usePresetFilters } from './usePresetFilters';
import { getShownColumns, hasActiveTableFilters, isHiddenColumn, rowKey } from './utils';
import { multiSorterStorage } from './Views/MultiSorter/utils';
import { removeDefaultFilters } from './Views/utils';

import { type DataTableProps, DataTableContext } from './index';

const getActiveFilters = (filters: Record<string, any> | undefined) =>
  // AntD sends empty arrays/objects for inactive filters during pagination and sort changes.
  // Compare only active values so those table changes do not reset pagination like real filters.
  Object.entries(filters ?? {}).reduce(
    (acc, [key, value]) => (Array.isArray(value) && value.length ? { ...acc, [key]: value } : acc),
    {}
  );

/**
 * "This place is not a place of honor...
 *  no highly esteemed deed is commemorated here...
 *  nothing valued is here."
 *
 * @param props
 * @returns
 */

export const DataTable: React.FunctionComponent<DataTableProps> = props => {
  const { t } = useTranslation();
  const {
    cardType,
    columns: columnDefs = [],
    bannerMessage,
    deferQueryUntilFiltered = false,
    controls,
    dataQuery,
    data,
    disableUserSort = false,
    fillWidth,
    id,
    initialPresetFilter,
    clearSortAndFilterType: _clearSortAndFilterType,
    bladeCardsScope,
    showSavedViews: _showSavedViews,
    savedViewCustomFields,
    onSavedViewChange,
    loading,
    setLoading,
    setTotalCount,
    onClick,
    // FIXME ARS - we are allowing this two-way binding so the AssetChooser and ExportReport can work
    onDataQueryChange,
    onSavePriority,
    // FIXME ARS - onTabChange this should eventually not be allowed to be a prop but allow it for now
    onTabChange: deprecatedOnTabChange,
    rowSelection,
    scrollWidth = TABLE_SCROLL_WIDTH,
    scrollHeight,
    tabBarExtraContent,
    tabBarPriorityContent,
    tabList,
    title,
    storageKey,
    persistTableState = true,
    initialSort,
    isChangePriorityVisible = true,
    setDisableTopLevelFilters,
    setShouldShowMultisort,
    onFiltersChange = () => {},
    onClearSelection = () => {},
    isExpandAllVisible = false,
    isExpandAllEnabled = false,
    handleExpandAll,
    allExpanded = false,
    changePriorityDisabledText = undefined,
    routeProps,
  } = props || {};

  const { SAVED_VIEWS } = useFeatures().features;
  const showSavedViews = SAVED_VIEWS && !!_showSavedViews && !!id;

  const {
    initialPagination,
    pagination,
    updatePagination,
    filteredInfo,
    updateFilteredInfo,
    sortedInfo,
    updateSortedInfo,
    tableView,
    customizedColumnSet,
    multiSortDef,
    updateMultiSortDef,
    setPersistedState,
  } = useContext(DataTableContext) || {};

  const multiSorterStorageManager = multiSorterStorage({ storageKey: storageKey?.MULTI_SORT });

  const {
    handleOpenPriority,
    handleCancelPriority,
    handleDragUpdate,
    handleSavePriority,
    isPrioritizing,
    disableSavePriority,
    loading: priorityLoading,
    movedItem,
    prioritizedItems,
  } = usePriority({ data, onSavePriority });

  // usePriority is untyped JS, so its state comes back as `null`.
  const movedRow = movedItem as MovedRow | null;

  // When we're prioritzing a table that is also filtered we need to disable multisort. This useMemo handles that for us. Initially implemented for WorkOrderTasksTable.
  useMemo(() => {
    // Return early if we don't need to check setShouldShowMultisort
    if (!setShouldShowMultisort) {
      return;
    }

    // If we are priortizing check if we're also filtered
    if (isPrioritizing) {
      // If we're not filtered we can return true
      if (!filteredInfo) {
        return setShouldShowMultisort(true);
      }
      // Check for any values in our filtered values
      // @ts-ignore issue with the type on filteredInfo thinking it's an unknown[]
      const filteredValues = Object.values(filteredInfo).some(x => x);
      return setShouldShowMultisort(!!filteredValues ? false : true);
    }

    // Otherwise we can still show multisort
    return setShouldShowMultisort(true);
  }, [isPrioritizing, filteredInfo, setShouldShowMultisort]);

  const { activeTabKey, onTabChange } = usePresetFilters({
    initialPresetFilter,
    tabList,
    deprecatedOnTabChange,
  });

  function getSortOrder(sortOrder: ESortOptions) {
    if (sortOrder === 'DESC') {
      return 'descend';
    }
    return 'ascend';
  }

  useEffect(() => {
    if (initialSort) {
      updateSortedInfo({ columnKey: initialSort.key, order: getSortOrder(initialSort.sort) });
    }
    // initialSort may not be the same object between renders, depending on where the context provider is.
    // stringifying serves the purpose of checking the relevant equality
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialSort)]);

  const clearSortAndFilterType =
    _clearSortAndFilterType === EFilterSortClearType.RESET &&
    (initialPresetFilter?.value?.filters || initialPresetFilter?.value?.sortOrder)
      ? EFilterSortClearType.RESET
      : EFilterSortClearType.CLEAR;

  const handleTableChange = (pagination: any, filters: any, sorter: any, _extra?: any) => {
    const tableChangeAction = _extra?.action;
    const isPaginationChange = tableChangeAction === 'paginate';
    const isSortChange = tableChangeAction === 'sort';
    const isFilterChange = tableChangeAction === 'filter';

    if (isPaginationChange) {
      props?.onPaginationChange?.();
    }

    // FIXME: after antd v4 upgrade, sorter can contain an undefined column
    const appliedSort = sorter && !sorter.column ? {} : { ...sorter };
    const appliedFilters = clearStaleAssetHierarchyFilters(filters, filteredInfo, {
      isInteractiveFilterChange: isFilterChange,
    });
    const filtersChanged =
      isFilterChange ||
      (!isPaginationChange &&
        !isSortChange &&
        !isEqual(getActiveFilters(filteredInfo), getActiveFilters(appliedFilters)));
    const appliedPagination =
      filtersChanged && pagination ? { ...pagination, current: 1 } : pagination;

    if (appliedSort.column?.sortKey) {
      // allow the sort key to be overridden from the column's default `index` value
      appliedSort.columnKey = appliedSort.column.sortKey;
    }

    const data = _extra?.currentDataSource ?? _extra;
    if (props.onCurrentDataChange) {
      props.onCurrentDataChange(data);
    }

    // Column filter changes must update table state to page 1 before useQuery sees new variables.
    // Otherwise Apollo first requests the new filters with the stale page offset.
    updatePagination(appliedPagination);
    updateSortedInfo(appliedSort);

    // A single-column sort and a multi-sort are mutually exclusive; choosing a
    // header sort clears any active multi-sort so they don't compete in the query.
    if (isSortChange && appliedSort.columnKey && multiSortDef) {
      updateMultiSortDef(undefined);
      multiSorterStorageManager.remove();
    }

    // antd omits filter keys on paginate/sort events. Updating filteredInfo from those callbacks
    // would drop built-in or persisted filters and incorrectly reset pagination to page 1.
    if (!isPaginationChange && !isSortChange && filtersChanged) {
      updateFilteredInfo(appliedFilters);
      onFiltersChange(appliedFilters);
    }

    const { columnKey, order } = appliedSort;
    persistTableState &&
      setPersistedState({
        filteredInfo: removeDefaultFilters(id, appliedFilters),
        sortedInfo: { columnKey, order },
        pagination: appliedPagination,
      });
  };

  const handleClearSortAndFilter = () => {
    updatePagination(
      isEmpty(initialPagination)
        ? initialPagination
        : { ...initialPagination, pageSize: pagination && pagination?.pageSize }
    );
    if (clearSortAndFilterType === EFilterSortClearType.CLEAR) {
      // clears filters/sorters
      updateFilteredInfo({});
      onFiltersChange({});
      updateSortedInfo({});
      persistTableState && setPersistedState({});

      // FIXME ARS - both of these following callbacks are... :-( they just highlight some code smell that we will chip away at
      if (onDataQueryChange) {
        onDataQueryChange({});
      }
    } else {
      // resets filters/sorters to their initial values
      const { filters = {}, sortOrder = {} } = initialPresetFilter?.value ?? {};
      updateFilteredInfo(filters);
      onFiltersChange(filters);
      updateSortedInfo(sortOrder);

      persistTableState &&
        setPersistedState({ filteredInfo: filters, sortedInfo: sortOrder, pagination });
    }
  };

  // When we're prioritzing a table we need to disable top level filters.
  useMemo(() => {
    if (!setDisableTopLevelFilters) {
      return;
    }

    if (isPrioritizing) {
      return setDisableTopLevelFilters(true);
    }

    // Otherwise we can still show multisort
    return setDisableTopLevelFilters(false);
  }, [isPrioritizing, setDisableTopLevelFilters]);

  const titleWithControls = (
    <HeaderContainer>
      {title && <TitleContainer>{title}</TitleContainer>}
      {controls && (
        <Control>
          <Form layout="inline">{controls}</Form>
        </Control>
      )}
    </HeaderContainer>
  );

  // necessary otherwise title will always show
  const showTitle = title || controls;

  const onRow = useCallback(
    (record: any): React.HTMLAttributes<HTMLElement> => {
      if (onClick) {
        return { onClick: () => onClick(record) };
      }

      return {};
    },
    [onClick]
  );

  const allColumns = getShownColumns(columnDefs, customizedColumnSet).map(column => ({
    ...column,
    serverSortAndFilter: !!dataQuery,
  }));

  const formattedAllColumns = useFormattedColumns({
    columns: allColumns,
    isPrioritizing,
  });

  const formattedColumns = formattedAllColumns.filter(col => !isHiddenColumn(col));

  const deferActive =
    Boolean(deferQueryUntilFiltered) &&
    !hasActiveTableFilters(filteredInfo as Record<string, unknown>);
  const effectiveRouteProps = useMemo(
    () => ({
      ...routeProps,
      skip: Boolean(routeProps?.skip) || deferActive,
    }),
    [routeProps, deferActive]
  );

  const tableProps = {
    loading,
    setLoading,
    setTotalCount,
    rowKey,
    onRow,
    scroll: {
      x: scrollHeight ? 'max-content' : scrollWidth,
      ...(scrollHeight && { y: scrollHeight }),
    },
    onTableChange: handleTableChange,
    onDataQueryChange,
    onDragUpdate: handleDragUpdate,
    onClearSortAndFilter: handleClearSortAndFilter,
    // antd below v5 does not work properly with a passed in TooltipProp and these get in the way of other UI so default false
    showSorterTooltip: false,
    ...props,
    routeProps: effectiveRouteProps,
    columns: formattedColumns,
    // Server filters are resolved against these defs, so they must include columns the column
    // picker has hidden: otherwise a hidden column's `filterKeyOverride`/`serverSideFilterParsers`
    // are lost and its still-active filter is sent to the API under the raw column key.
    // Client tables keep the rendered set, which their `onFilter` functions are bound to.
    filterColumns: dataQuery ? columnDefs : formattedAllColumns,
    // While deferring, the query is skipped so no rows load. Override total to 0 at the
    // render boundary (never through updatePagination/persistedState) so stale persisted
    // counts don't render page links for data that isn't there. The real total flows back
    // once a filter is applied and the count query runs.
    pagination: deferActive && pagination ? { ...pagination, total: 0 } : pagination,
    filteredInfo,
    sortOrder: sortedInfo,
    tableLayout: scrollHeight ? 'fixed' : props.tableLayout ?? 'auto', // antd v3.24+ requires this to make sure no cols overlap
  };

  useEffect(() => {
    const firstTabKey = tabList?.[0]?.key;
    if (isPrioritizing && firstTabKey && activeTabKey !== firstTabKey) {
      onTabChange(firstTabKey);
    }
  }, [isPrioritizing, activeTabKey, tabList, onTabChange]);

  const showBanner = !!(multiSortDef || customizedColumnSet || tableView || bannerMessage);

  // Currently (11/2022), the only location where actions should
  // not exist in the table header area is the inspection details page.
  // AntD specifies the table header and the tab area as separate blocks,
  // so we need to combine the two for this single instance.
  const shouldOverrideActionsLocation = !!tabBarExtraContent;
  const TableActions = (
    <>
      <Actions
        tableId={id}
        // @ts-ignore JD - There's a minor type mis-match between AntD's column expectations, and ours.
        columnDefs={columnDefs}
        filters={filteredInfo}
        sorters={sortedInfo ? [sortedInfo] : undefined}
        storageKey={storageKey}
        onClearSortAndFilter={handleClearSortAndFilter}
        onClearSelection={onClearSelection}
        onTableChange={handleTableChange}
        priorityContent={tabBarPriorityContent}
        additionalContent={tabBarExtraContent}
        hasSelectedRows={rowSelection?.selectedRowKeys?.length}
        rowReordering={
          onSavePriority
            ? {
                rowCount: data ? data.length : 0,
                priorityLoading,
                onSavePriority: handleSavePriority,
                onCancelPriority: handleCancelPriority,
                onOpenPriority: handleOpenPriority,
                isPrioritizing,
                disableSavePriority,
                changePriorityDisabledText,
              }
            : null
        }
        {...props}
        clearSortAndFilterType={clearSortAndFilterType}
        bladeCardsScope={bladeCardsScope}
        showSavedViews={showSavedViews}
        savedViewCustomFields={savedViewCustomFields}
        onSavedViewChange={onSavedViewChange}
        isChangePriorityVisible={isChangePriorityVisible}
        isExpandAllVisible={isExpandAllVisible}
        handleExpandAll={handleExpandAll}
        isExpandAllEnabled={isExpandAllEnabled}
        allExpanded={allExpanded}
      />
    </>
  );

  const { hasServerDataTableContextProviderParent, renderInServerContext, ...serverTableProps } =
    tableProps;

  return (
    <DataTableWrapper
      renderInServerContext={renderInServerContext}
      shouldRenderInServerContext={Boolean(dataQuery && !hasServerDataTableContextProviderParent)}
    >
      <TableCard
        data-testid="data-table"
        id={id}
        bodyStyle={{ padding: '1px 0 0 0' }}
        title={showTitle && titleWithControls}
        extra={!shouldOverrideActionsLocation ? TableActions : undefined}
        tabList={tabList}
        tabBarExtraContent={shouldOverrideActionsLocation ? TableActions : undefined}
        activeTabKey={activeTabKey}
        onTabChange={onTabChange}
        type={cardType}
        clickable={!!onClick}
        fillWidth={!!fillWidth}
        disableUserSort={disableUserSort}
      >
        {deferActive && (
          <DeferBanner
            type="info"
            showIcon={true}
            message={t(
              'table.largeRecordCountSetFilter',
              'Due to the large number of records in this table, please set a filter to view data.'
            )}
          />
        )}
        {showBanner && (
          <CustomizedDataBanner
            tableId={id}
            storageKey={storageKey}
            // @ts-ignore JD - There's a minor type mis-match between AntD's column expectations, and ours.
            columnDefs={columnDefs}
            onTableChange={handleTableChange}
            onClearSortAndFilter={handleClearSortAndFilter}
            onClearSelection={onClearSelection}
            savedViewCustomFields={savedViewCustomFields}
            onSavedViewChange={onSavedViewChange}
            additionalBannerMessage={bannerMessage}
          />
        )}
        {dataQuery ? (
          <ServerDataTable
            {...omit(serverTableProps, 'title')}
            dataQuery={dataQuery}
            tableLayout={serverTableProps.tableLayout as TableLayout}
          />
        ) : (
          <ClientDataTable
            {...omit(tableProps, 'title')}
            data={prioritizedItems || data}
            draggable={isPrioritizing}
            rowSelection={isPrioritizing ? undefined : rowSelection}
            tableLayout={tableProps.tableLayout as TableLayout}
            isPrioritizing={isPrioritizing}
            handleOrderUpdate={handleDragUpdate}
            movedRow={movedRow}
          />
        )}
      </TableCard>
    </DataTableWrapper>
  );
};

interface DataTableWrapperProps {
  children: React.ReactNode;
  renderInServerContext?: React.ReactNode;
  shouldRenderInServerContext: boolean;
}

function DataTableWrapper({
  children,
  renderInServerContext,
  shouldRenderInServerContext,
}: DataTableWrapperProps) {
  if (shouldRenderInServerContext) {
    return (
      <ServerDataTableContextProvider>
        {children}
        {renderInServerContext}
      </ServerDataTableContextProvider>
    );
  }

  return <>{children}</>;
}
